import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AccountBalance,
  AccountType,
  ValidatorsProps,
  Response,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import { Balance, CustomerType } from '../../../types';
import { customersComponentApi } from '../../services/customers';

export interface TransactionFromFeeState {
  customerId?: string;
  customerType?: CustomerType;
  debitAccountBalance?: AccountBalance;
  creditAccountBalance?: AccountBalance;
  validators?: {
    createGeneralLedgerTransactionSchema: ValidatorsProps;
  };
  baseCurrencyId?: string;
  accountCategories?: Category[];
  debitBalances?: Balance[];
  creditBalances?: Balance[];
}

const initialState: TransactionFromFeeState = {};

export const customerTransactionFromFeeSlice = createSlice({
  name: 'customerTransactionFromFee',
  initialState,
  reducers: {
    clearAccountBalance: (state, action: PayloadAction<AccountType>) => {
      if (action.payload === AccountType.DEBIT) {
        state.debitAccountBalance = undefined;
      } else if (action.payload === AccountType.CREDIT) {
        state.creditAccountBalance = undefined;
      }
    },
    clearTransactionFromFeeState: (state) => {
      state.baseCurrencyId = undefined;
      state.debitAccountBalance = undefined;
      state.creditAccountBalance = undefined;
      state.debitBalances = undefined;
      state.creditBalances = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        customersComponentApi.endpoints.getCustomersBaseCurrency.matchFulfilled,
        (state, action: PayloadAction<{ data: { id: string } }>) => {
          state.baseCurrencyId = action.payload.data.id;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomersAccountBalanceById
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<AccountBalance>> & {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  query?: string | undefined;
                  typeAcc: AccountType;
                };
              };
            };
          },
        ) => {
          if (action.meta.arg.originalArgs.typeAcc) {
            const [accBalance] = action.payload.data.edges.map(
              (item) => item.node,
            );

            if (action.meta.arg.originalArgs.typeAcc === AccountType.DEBIT) {
              state.debitAccountBalance = accBalance;
            } else if (
              action.meta.arg.originalArgs.typeAcc === AccountType.CREDIT
            ) {
              state.creditAccountBalance = accBalance;
            }
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomersGLTransactionValidators
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              createGeneralLedgerTransactionSchema: ValidatorsProps;
            };
          }>,
        ) => {
          state.validators = action.payload.data;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomersAccountsCategories
          .matchFulfilled,
        (state, action: PayloadAction<Response<Category>>) => {
          const accountCategories = action.payload.data.edges.map(
            (item) => item.node,
          );

          const feeAccountsCategories = accountCategories
            .filter((item) => item.number === '4')
            .map((category) => ({
              ...category,
              accountGroups: category.accountGroups.filter(
                (group) => group.number === '0',
              ),
            }));

          state.accountCategories = feeAccountsCategories;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerAllCreditAccountBalances
          .matchFulfilled,
        (state, action: PayloadAction<Response<Balance>>) => {
          state.creditBalances = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerAllDebitAccountBalances
          .matchFulfilled,
        (state, action: PayloadAction<Response<Balance>>) => {
          state.debitBalances = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      );
  },
});

export const { clearAccountBalance, clearTransactionFromFeeState } =
  customerTransactionFromFeeSlice.actions;

export default customerTransactionFromFeeSlice.reducer;
